import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Save, Eye, ArrowLeft, Image as ImageIcon, Type, 
  Loader2, Trash2, Settings, Users, Shield, BookOpen, Plus,
  Upload, FileText, Trophy
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import MilestoneCelebration from '../components/MilestoneCelebration';
import RichTextEditor from '../components/RichTextEditor';
import { countWords } from '../lib/storyHtml';
import { EditorSettingsSheet } from '../components/editor/EditorSettingsSheet';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import ExportMenu from '../components/ExportMenu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const FONTS = [
  { name: 'Lora', value: 'Lora' },
  { name: 'Georgia', value: 'Georgia' },
  { name: 'Times New Roman', value: 'Times New Roman' },
  { name: 'Cormorant Garamond', value: 'Cormorant Garamond' },
  { name: 'Merriweather', value: 'Merriweather' },
];

const FONT_SIZES = ['14px', '16px', '18px', '20px', '22px', '24px'];

const StoryEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const isEditing = !!id;

  // Check for challenge parameter in URL
  const searchParams = new URLSearchParams(window.location.search);
  const challengeFromUrl = searchParams.get('challenge');

  const [story, setStory] = useState({
    title: '',
    content: '',
    summary: '',
    cover_image: '',
    cover_image_position: 50,
    genre: 'General Fiction',
    tags: [],
    is_draft: true,
    font_family: 'Lora',
    font_size: '18px',
    collaborator_ids: [],
    challenge_id: challengeFromUrl || null
  });
  const [challengeInfo, setChallengeInfo] = useState(null);
  const [genres, setGenres] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [originalWorkConfirmed, setOriginalWorkConfirmed] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [newMilestone, setNewMilestone] = useState(null);
  const [collabSearch, setCollabSearch] = useState('');
  const [collabResults, setCollabResults] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [isDraggingCover, setIsDraggingCover] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [chapters, setChapters] = useState([{ title: '', content: '' }]);
  const [activeChapter, setActiveChapter] = useState(0);
  const contentRef = useRef(null);
  const fileInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const coverContainerRef = useRef(null);
  const importInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const headerRef = useRef(null);

  // Lets the editor toolbar stick just below the editor header (its height changes with the challenge banner/screen size)
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const update = () => document.documentElement.style.setProperty('--editor-header-h', `${el.offsetHeight}px`);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading]);
  const handleFileImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/import/file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const imported = res.data;
      
      // Set title if empty
      setStory(prev => ({
        ...prev,
        title: prev.title || imported.title,
        content: imported.content
      }));
      
      // Parse chapters from imported content
      const parsedChapters = parseChaptersFromContent(imported.content);
      setChapters(parsedChapters);
      setActiveChapter(0);
      
      const chapterCount = imported.chapters?.length || 0;
      const msg = chapterCount > 1 
        ? `Imported "${imported.title}" (${imported.word_count} words, ${chapterCount} chapters)`
        : `Imported "${imported.title}" (${imported.word_count} words)`;
      alert(msg);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to import file. Please try a .docx, .txt, or .pdf file.');
    } finally {
      setImporting(false);
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  // Parse chapters from flat content string
  const parseChaptersFromContent = (content) => {
    if (!content) return [{ title: '', content: '' }];
    const parts = content.split(/\[chapter:(.*?)\]/);
    if (parts.length <= 1) return [{ title: '', content: content }];
    
    const chs = [];
    const preamble = parts[0].trim();
    if (preamble) chs.push({ title: '', content: preamble });
    for (let i = 1; i < parts.length; i += 2) {
      chs.push({ title: parts[i], content: (parts[i + 1] || '').trim() });
    }
    return chs.length > 0 ? chs : [{ title: '', content: '' }];
  };

  // Join chapters back into flat content string for saving
  const joinChaptersToContent = (chs) => {
    if (chs.length === 1 && !chs[0].title) return chs[0].content;
    return chs.map((ch, i) => {
      if (i === 0 && !ch.title) return ch.content;
      return `[chapter:${ch.title || `Chapter ${i}`}]\n${ch.content}`;
    }).join('\n');
  };

  const handleAddChapter = () => {
    const newChapters = [...chapters, { title: '', content: '' }];
    setChapters(newChapters);
    setActiveChapter(newChapters.length - 1);
    setStory(prev => ({ ...prev, content: joinChaptersToContent(newChapters) }));
    // Auto-focus content area after render
    setTimeout(() => {
      if (contentRef.current) contentRef.current.focus();
    }, 100);
    saveAfterChapterChange(newChapters);
  };

  const handleRenameChapter = (idx) => {
    const current = chapters[idx].title || `Chapter ${idx + 1}`;
    const newTitle = window.prompt('Rename chapter:', current);
    if (newTitle === null) return;
    const newChapters = [...chapters];
    newChapters[idx] = { ...newChapters[idx], title: newTitle };
    setChapters(newChapters);
    setStory(prev => ({ ...prev, content: joinChaptersToContent(newChapters) }));
    saveAfterChapterChange(newChapters);
  };

  const handleDeleteChapter = (idx) => {
    if (chapters.length <= 1) return;
    if (!window.confirm(`Delete "${chapters[idx].title || 'this chapter'}" and its content?`)) return;
    const newChapters = chapters.filter((_, i) => i !== idx);
    setChapters(newChapters);
    setActiveChapter(Math.min(activeChapter, newChapters.length - 1));
    setStory(prev => ({ ...prev, content: joinChaptersToContent(newChapters) }));
    saveAfterChapterChange(newChapters);
  };

  // Surgical auto-save helper — uses the freshly-passed `newChapters` (no closure read on chapters)
  // and sends a partial PUT so other story fields (title, genre, is_draft) cannot regress.
  const saveAfterChapterChange = async (newChapters) => {
    if (!isEditing || !story.title?.trim()) return;
    try {
      const updatedContent = joinChaptersToContent(newChapters);
      const response = await api.put(`/stories/${id}`, { content: updatedContent });
      setStory(response.data);
      setLastSaved(new Date());
    } catch (e) {
      console.error('Auto-save after chapter change failed:', e);
    }
  };

  const handleChapterContentChange = (text) => {
    const newChapters = [...chapters];
    newChapters[activeChapter] = { ...newChapters[activeChapter], content: text };
    setChapters(newChapters);
    setStory(prev => ({ ...prev, content: joinChaptersToContent(newChapters) }));
  };

  const handleCoverDragStart = (e) => {
    e.preventDefault();
    setIsDraggingCover(true);
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    setDragStart({ y: clientY, startPos: story.cover_image_position ?? 50 });
  };

  const handleCoverDragMove = useCallback((e) => {
    if (!isDraggingCover || !dragStart || !coverContainerRef.current) return;
    e.preventDefault();
    const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
    const containerHeight = coverContainerRef.current.offsetHeight;
    const deltaY = clientY - dragStart.y;
    const deltaPercent = (deltaY / containerHeight) * 100;
    const newPos = Math.max(0, Math.min(100, dragStart.startPos - deltaPercent));
    setStory(prev => ({ ...prev, cover_image_position: Math.round(newPos) }));
  }, [isDraggingCover, dragStart]);

  const handleCoverDragEnd = useCallback(() => {
    setIsDraggingCover(false);
    setDragStart(null);
  }, []);

  useEffect(() => {
    if (isDraggingCover) {
      window.addEventListener('mousemove', handleCoverDragMove);
      window.addEventListener('mouseup', handleCoverDragEnd);
      window.addEventListener('touchmove', handleCoverDragMove, { passive: false });
      window.addEventListener('touchend', handleCoverDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleCoverDragMove);
        window.removeEventListener('mouseup', handleCoverDragEnd);
        window.removeEventListener('touchmove', handleCoverDragMove);
        window.removeEventListener('touchend', handleCoverDragEnd);
      };
    }
  }, [isDraggingCover, handleCoverDragMove, handleCoverDragEnd]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
      alert('Only JPEG, PNG, GIF, and WebP images are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be under 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload/story-image', formData);
      const imageTag = `\n[image:${res.data.image_url}]\n`;
      const textarea = contentRef.current;
      if (textarea) {
        const pos = textarea.selectionStart || chapters[activeChapter].content.length;
        const currentContent = chapters[activeChapter].content;
        const newText = currentContent.slice(0, pos) + imageTag + currentContent.slice(pos);
        handleChapterContentChange(newText);
      } else {
        handleChapterContentChange(chapters[activeChapter].content + imageTag);
      }
    } catch (err) {
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be under 5MB');
      return;
    }
    setUploadingCover(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload/story-image', formData);
      setStory(prev => ({ ...prev, cover_image: res.data.image_url }));
    } catch (err) {
      alert('Failed to upload cover photo. Please try again.');
    } finally {
      setUploadingCover(false);
      if (coverInputRef.current) coverInputRef.current.value = '';
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const genresRes = await api.get('/genres');
        setGenres(genresRes.data);

        if (isEditing) {
          const storyRes = await api.get(`/stories/${id}`);
          setStory(storyRes.data);
          setChapters(parseChaptersFromContent(storyRes.data.content));
        }

        // Fetch challenge info if writing for a challenge
        if (challengeFromUrl) {
          try {
            const challengeRes = await api.get(`/challenges/${challengeFromUrl}`);
            setChallengeInfo(challengeRes.data);
          } catch (e) {
            console.error('Failed to fetch challenge info:', e);
          }
        }
      } catch (error) {
        
        if (isEditing) navigate('/my-stories');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, api, isEditing, navigate, challengeFromUrl]);

  const handleSave = async (publish = false) => {
    if (!story.title.trim()) {
      alert('Please enter a title');
      return;
    }

    // Empty-chapter guard (only enforced for multi-chapter works)
    if (chapters.length > 1) {
      const empties = chapters.filter(ch => !(ch.content || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim());
      if (empties.length > 0) {
        const titles = empties.map((ch, i) => `"${ch.title || `Chapter ${chapters.indexOf(ch) + 1}`}"`).join(', ');
        const proceed = window.confirm(
          `${empties.length === 1 ? 'This chapter is' : 'These chapters are'} empty: ${titles}.\n\nReaders will see "This chapter has no content yet." until you fill it in.\n\nSave anyway?`
        );
        if (!proceed) return;
      }
    }

    setSaving(true);
    try {
      const storyData = {
        ...story,
        is_draft: publish ? false : story.is_draft
      };

      let response;
      if (isEditing) {
        response = await api.put(`/stories/${id}`, storyData);
      } else {
        response = await api.post('/stories', storyData);
        navigate(`/story/${response.data.id}/edit`, { replace: true });
      }

      setStory(response.data);
      setLastSaved(new Date());

      // Log word count for writing goals
      const totalWords = chapters.reduce((sum, ch) => sum + countWords(ch.content), 0);
      if (totalWords > 0) {
        try { await api.post('/writing-goals/log', { words: totalWords }); } catch (e) { console.error('Word count log failed:', e); }
      }

      // Check for new milestone
      if (response.data.new_milestone) {
        setNewMilestone(response.data.new_milestone);
      }

      if (publish) {
        navigate(`/story/${response.data.id}`);
      }
    } catch (error) {
      
      alert('Failed to save story. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this story? This cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/stories/${id}`);
      navigate('/my-stories');
    } catch (error) {
      
      alert('Failed to delete story. Please try again.');
    }
  };

  const addTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !story.tags.includes(tag) && story.tags.length < 5) {
      setStory({ ...story, tags: [...story.tags, tag] });
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setStory({ ...story, tags: story.tags.filter(t => t !== tagToRemove) });
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Skeleton className="h-12 w-full mb-4" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-base" data-testid="story-editor-page">
      {/* Milestone Celebration */}
      {newMilestone && (
        <MilestoneCelebration
          milestone={newMilestone}
          onClose={() => setNewMilestone(null)}
        />
      )}

      {/* Editor Header */}
      <header ref={headerRef} className="sticky top-0 z-30 bg-white border-b border-brand-primary/10">
        {/* Challenge Banner */}
        {challengeInfo && (
          <div className="bg-brand-accent/10 border-b border-brand-accent/20 px-4 py-2">
            <div className="max-w-6xl mx-auto flex items-center gap-2 text-sm">
              <Trophy className="w-4 h-4 text-brand-accent" />
              <span className="text-text-secondary">Writing for challenge:</span>
              <span className="font-semibold text-text-primary">{challengeInfo.title}</span>
              {challengeInfo.word_limit_max && (
                <span className="text-text-muted ml-2">
                  (Word limit: {challengeInfo.word_limit_min || 0}–{challengeInfo.word_limit_max})
                </span>
              )}
            </div>
          </div>
        )}
        <div className="max-w-6xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/my-stories')}
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <p className="text-sm text-text-muted">
                {isEditing ? 'Editing' : 'New Story'}
                {story.is_draft && <span className="ml-2 text-amber-600">(Draft)</span>}
              </p>
              {lastSaved && (
                <p className="text-xs text-text-muted">
                  Last saved: {lastSaved.toLocaleTimeString()}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
            {/* Import File Button */}
            {!isEditing && (
              <>
                <input
                  ref={importInputRef}
                  type="file"
                  accept=".docx,.txt,.pdf"
                  onChange={handleFileImport}
                  className="hidden"
                  data-testid="import-file-input"
                />
                <Button
                  variant="outline"
                  onClick={() => importInputRef.current?.click()}
                  className="gap-2"
                  disabled={importing}
                  data-testid="import-file-button"
                >
                  {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  <span className="hidden sm:inline">Import File</span>
                </Button>
              </>
            )}

            {isEditing && (
              <Button
                variant="ghost"
                onClick={() => navigate(`/story/${id}`)}
                className="gap-2"
                data-testid="preview-button"
              >
                <Eye className="w-4 h-4" />
                <span className="hidden sm:inline">Preview</span>
              </Button>
            )}

            <EditorSettingsSheet
              story={story} setStory={setStory} genres={genres} isEditing={isEditing} user={user}
              coverInputRef={coverInputRef} uploadingCover={uploadingCover}
              tagInput={tagInput} setTagInput={setTagInput} addTag={addTag} removeTag={removeTag}
              collabSearch={collabSearch} setCollabSearch={setCollabSearch}
              collabResults={collabResults} setCollabResults={setCollabResults}
              handleDelete={handleDelete} api={api}
              handleSave={handleSave} saving={saving}
            />

            {isEditing && !story.is_draft && (
              <ExportMenu storyId={id} />
            )}

            <Button
              variant="outline"
              onClick={() => handleSave(false)}
              disabled={saving}
              className="gap-2"
              data-testid="save-draft-button"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              <span className="hidden sm:inline">Save Draft</span>
              <span className="sm:hidden">Save</span>
            </Button>

            <Button
              onClick={() => setShowPublishDialog(true)}
              disabled={saving}
              className="gap-2 btn-accent"
              data-testid="publish-button"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Publish
            </Button>

            <AlertDialog open={showPublishDialog} onOpenChange={setShowPublishDialog}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-serif text-xl">Publish Your Work</AlertDialogTitle>
                  <AlertDialogDescription className="text-sm leading-relaxed">
                    By publishing, you confirm this is your original work — not generated by AI, not plagiarized, and not copied from another source.
                    <br /><br />
                    <strong>You own 100% of your work.</strong>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => handleSave(true)}
                    className="btn-accent"
                    data-testid="confirm-publish-button"
                  >
                    I Confirm — Publish
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </header>

      {/* Editor Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Title */}
        <input
          type="text"
          placeholder="Title"
          value={story.title}
          onChange={(e) => setStory({ ...story, title: e.target.value })}
          className="w-full font-serif text-4xl font-semibold text-text-primary bg-transparent border-none outline-none placeholder:text-text-muted/50 mb-6"
          data-testid="story-title-input"
        />

        {/* Summary */}
        <textarea
          placeholder="Write a brief summary to hook your readers..."
          value={story.summary}
          onChange={(e) => setStory({ ...story, summary: e.target.value })}
          className="w-full text-lg text-text-secondary bg-transparent border-none outline-none placeholder:text-text-muted/50 resize-none mb-8"
          rows={2}
          data-testid="story-summary-input"
        />

        {/* Cover Photo */}
        <input
          type="file"
          ref={coverInputRef}
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={handleCoverUpload}
          className="hidden"
          data-testid="cover-file-input"
        />
        {story.cover_image ? (
          <div 
            ref={coverContainerRef}
            className={`relative mb-8 rounded-xl overflow-hidden group ${isDraggingCover ? 'cursor-grabbing' : 'cursor-grab'}`} 
            data-testid="cover-image-preview"
            onMouseDown={handleCoverDragStart}
            onTouchStart={handleCoverDragStart}
          >
            <img 
              src={story.cover_image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${story.cover_image}` : story.cover_image}
              alt="Cover" 
              className="w-full h-48 sm:h-64 object-cover select-none pointer-events-none"
              style={{ objectPosition: `center ${story.cover_image_position ?? 50}%` }}
              draggable={false}
            />
            {/* Drag hint */}
            <div className={`absolute inset-0 transition-colors flex flex-col items-center justify-center gap-2 ${isDraggingCover ? 'bg-black/20' : 'bg-black/0 group-hover:bg-black/30 opacity-0 group-hover:opacity-100'}`}>
              {!isDraggingCover && (
                <>
                  <p className="text-white text-sm font-medium bg-black/50 px-3 py-1 rounded-full">
                    Drag to reposition
                  </p>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); coverInputRef.current?.click(); }}
                      disabled={uploadingCover}
                      className="bg-white text-text-primary hover:bg-white/90 gap-1.5"
                      data-testid="change-cover-btn"
                    >
                      {uploadingCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
                      Change
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      onClick={(e) => { e.stopPropagation(); setStory(prev => ({ ...prev, cover_image: '', cover_image_position: 50 })); }}
                      data-testid="remove-cover-btn"
                    >
                      Remove
                    </Button>
                  </div>
                </>
              )}
            </div>
            {/* Mobile: always-visible edit button */}
            <div className="absolute top-2 right-2 flex gap-2 sm:hidden">
              <Button
                type="button"
                size="sm"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); coverInputRef.current?.click(); }}
                disabled={uploadingCover}
                className="bg-white/90 text-text-primary hover:bg-white gap-1 shadow-sm"
                data-testid="mobile-change-cover-btn"
              >
                <ImageIcon className="w-3 h-3" />
                Change
              </Button>
              <Button
                type="button"
                size="sm"
                variant="destructive"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setStory(prev => ({ ...prev, cover_image: '', cover_image_position: 50 })); }}
                className="shadow-sm"
                data-testid="mobile-remove-cover-btn"
              >
                Remove
              </Button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => coverInputRef.current?.click()}
            disabled={uploadingCover}
            className="w-full mb-8 py-8 border-2 border-dashed border-brand-primary/15 rounded-xl hover:border-brand-accent/30 hover:bg-brand-accent/5 transition-colors flex flex-col items-center gap-2 text-text-muted"
            data-testid="add-cover-btn"
          >
            {uploadingCover ? (
              <Loader2 className="w-6 h-6 animate-spin text-brand-accent" />
            ) : (
              <ImageIcon className="w-6 h-6" />
            )}
            <span className="text-sm font-medium">{uploadingCover ? 'Uploading...' : 'Add Cover Photo'}</span>
            <span className="text-xs">JPEG, PNG, GIF, WebP — max 5MB</span>
          </button>
        )}

        {/* Main Content */}
        <div className="bg-white rounded-xl border border-brand-primary/10 shadow-soft-sm">
          {/* Chapter Tabs */}
          <div className="flex items-center gap-1 px-4 pt-4 pb-0 overflow-x-auto border-b border-[#E5E2DC]">
            {chapters.map((ch, idx) => (
              <div
                key={`ch-${ch.title}-${idx}`}
                className={`group flex items-center gap-1.5 px-4 py-2.5 rounded-t-lg cursor-pointer transition-colors text-sm whitespace-nowrap ${
                  activeChapter === idx
                    ? 'bg-brand-primary/10 text-brand-primary font-medium border-b-2 border-brand-primary'
                    : 'text-text-muted hover:bg-surface-alt hover:text-text-primary'
                }`}
                onClick={() => setActiveChapter(idx)}
                onDoubleClick={() => handleRenameChapter(idx)}
                data-testid={`chapter-tab-${idx}`}
              >
                <span>{ch.title || (chapters.length > 1 ? `Chapter ${idx + 1}` : 'Main')}</span>
                {chapters.length > 1 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteChapter(idx); }}
                    className="opacity-0 group-hover:opacity-100 text-text-muted hover:text-red-500 transition-opacity ml-1"
                    title="Delete chapter"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            <button
              onClick={handleAddChapter}
              className="flex items-center gap-1 px-3 py-2.5 text-sm text-text-muted hover:text-brand-accent hover:bg-surface-alt rounded-t-lg transition-colors"
              data-testid="add-chapter-tab"
              title="Add new chapter"
            >
              <Plus className="w-4 h-4" />
              Chapter
            </button>
          </div>

          {/* Toolbar */}
          <div className="flex items-center gap-2 px-8 pt-4 pb-3 border-b border-[#E5E2DC]">
            <input
              type="file"
              ref={fileInputRef}
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleImageUpload}
              className="hidden"
              data-testid="image-file-input"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingImage}
              className="gap-2 text-sm"
              data-testid="insert-image-button"
            >
              {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
              {uploadingImage ? 'Uploading...' : 'Insert Image'}
            </Button>
            {chapters.length > 1 && (
              <span className="text-xs text-text-muted ml-2">
                Editing: {chapters[activeChapter]?.title || `Chapter ${activeChapter + 1}`} • Double-click tab to rename
              </span>
            )}
          </div>

          <div className="p-0 pt-0">
            {/* Optional Chapter Title (for multi-chapter works) */}
            {(chapters.length > 1 || chapters[0]?.title) && (
              <div className="px-8 pt-4">
                <input
                  type="text"
                  value={chapters[activeChapter]?.title || ''}
                  onChange={(e) => {
                    const newChapters = [...chapters];
                    newChapters[activeChapter] = { ...newChapters[activeChapter], title: e.target.value };
                    setChapters(newChapters);
                    setStory(prev => ({ ...prev, content: joinChaptersToContent(newChapters) }));
                  }}
                  placeholder="Add chapter title (optional)"
                  className="w-full text-sm text-text-muted bg-transparent border-none outline-none mb-3 placeholder:text-text-muted/40 focus:text-lg focus:font-serif focus:font-semibold focus:text-text-primary transition-all"
                  data-testid="chapter-title-input"
                />
              </div>
            )}
            <RichTextEditor
              content={chapters[activeChapter]?.content || ''}
              onChange={(html) => handleChapterContentChange(html)}
              fontFamily={story.font_family}
              fontSize={story.font_size}
              placeholder="Start writing here..."
            />
          </div>
        </div>

        {/* Word Count & Ownership Reminder */}
        <div className="mt-4 flex items-center justify-between text-sm text-text-muted">
          <p data-testid="editor-ownership-notice" className="flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5 text-brand-primary" />
            You own 100% of your work. Original writing only—no AI-generated content.
          </p>
          <span>{chapters.reduce((sum, ch) => sum + countWords(ch.content), 0)} words</span>
        </div>
      </main>
    </div>
  );
};

export default StoryEditorPage;
